import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useApi } from './ApiContext';

interface User {
  id: string;
  username: string;
  discriminator: string;
  avatar: string;
}

interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: User | null;
  login: () => Promise<void>;
  logout: () => Promise<void>;
  checkAuth: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState<User | null>(null);
  const { get, post } = useApi();

  // Exposed so consumers (e.g. OAuth callback page) can force a re-check
  // from event handlers. Used from the effect below as well, but indirectly:
  // the effect inlines the same fetch so every setState lives inside an
  // async callback (react-hooks/set-state-in-effect flags direct calls to
  // functions that synchronously set state, which is why we can't just call
  // checkAuth() from the effect body).
  const checkAuth = useCallback(async () => {
    try {
      const userData = await get('/auth/me');
      setUser(userData);
      setIsAuthenticated(true);
    } catch {
      setUser(null);
      setIsAuthenticated(false);
    } finally {
      setIsLoading(false);
    }
  }, [get]);

  useEffect(() => {
    const onSessionExpired = () => {
      setUser(null);
      setIsAuthenticated(false);
      setIsLoading(false);
    };
    window.addEventListener('saga-auth-expired', onSessionExpired);
    return () => window.removeEventListener('saga-auth-expired', onSessionExpired);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const userData = await get('/auth/me');
        if (cancelled) return;
        setUser(userData);
        setIsAuthenticated(true);
      } catch {
        if (cancelled) return;
        setUser(null);
        setIsAuthenticated(false);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [get]);

  const login = async () => {
    try {
      const response = await get('/auth/discord/url');
      window.location.href = response.url;
    } catch (error) {
      console.error('Login failed:', error);
      throw error;
    }
  };

  const logout = async () => {
    try {
      await post('/auth/logout');
      // Clear stored tokens
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('token_expiry');
      setUser(null);
      setIsAuthenticated(false);
    } catch (error) {
      console.error('Logout failed:', error);
      // Clear tokens even if logout request fails
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('token_expiry');
      setUser(null);
      setIsAuthenticated(false);
      throw error;
    }
  };

  return (
    <AuthContext.Provider value={{ isAuthenticated, isLoading, user, login, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 